import { getHallData, getMultiTableDetail } from '@/api'
import router, { ROUTES } from '@/router'
import { store } from '@/store'
import { UserMutation } from '@/store/module/user'
import { EventBuss, SOCKET_MESSAGE } from '@/utils/constants'
import axios from 'axios'
import { ResultMemberList, SocketMessage, Table } from 'vue'
import { Options, Vue } from 'vue-class-component'
import { useI18n } from 'vue-i18n'
import { Action, Getter } from 'vuex-class'
import {
    buildPanelListData,
    collectGoodRoadTableIds,
    countGoodRoadTables,
    GOOD_ROAD_GAME_TYPES,
    GoodRoadTableEntry,
    mergeMultiDetailIntoTables,
    mergeTableBetUpdate,
    splitGoodRoadTablesByType
} from './utils'

@Options({})
export class GoodRoadMixin extends Vue {
    iN = useI18n().t

    selectedIndex = 'G002'
    desksByType: Record<string, Table[]> = {
        G002: [],
        G003: []
    }
    // track previously-known good road table ids to detect newly appearing good roads
    prevGoodRoadIds: Set<number> = new Set()
    serviceTimer: number | boolean = false
    countDownTimer: number | boolean = false
    timerStarted = false
    failureCounter = 0

    emitter!: Vue['emitter']
    $emit!: Vue['$emit']

    @Action('hideLoading') hideLoading!: () => void
    @Action('showLoading') showLoading!: () => void
    @Getter('token') token!: string
    @Getter('memberId') memberId!: number
    @Getter('limitId') limitId!: number

    beforeMount(): void {
        this.emitter.on(EventBuss.SOCKET_MESSAGE, this.handlesSocketMessages)
        this.emitter.on(EventBuss.PAGE_ON_VISIBLE, this.onPageVisible)
        this.emitter.on(
            EventBuss.SOCKET_RECONNECTED,
            this.handleReconnectSuccess
        )
    }

    mounted(): void {
        const params = new URLSearchParams(window.location.search)
        const gameType = params.get('gameType')
        if (gameType) this.selectedIndex = gameType
        this.loadAllData()
    }

    beforeUnmount(): void {
        this.stopApiRequest()
        this.cancelCountDown()
        this.emitter.off(EventBuss.SOCKET_MESSAGE, this.handlesSocketMessages)
        this.emitter.off(EventBuss.PAGE_ON_VISIBLE, this.onPageVisible)
        this.emitter.off(
            EventBuss.SOCKET_RECONNECTED,
            this.handleReconnectSuccess
        )
    }

    onPageVisible(): void {
        this.loadAllData(true)
    }

    handleReconnectSuccess(): void {
        this.loadAllData(true)
    }

    handlesSocketMessages(_message: SocketMessage | any): void {
        const _type = _message.type?.toUpperCase()
        const sDesk = _message.gameTable as Table | undefined
        if (!sDesk) return

        const gameType = sDesk.gameTypeNo
        if (gameType !== 'G002' && gameType !== 'G003') return

        const desks = this.desksByType[gameType] || []
        const index = desks.findIndex(item => item.id === sDesk.id)
        if (index === -1) return

        const updated = this.mergeSocketTable(desks[index], sDesk, _type)
        if (!updated) return

        desks.splice(index, 1, updated)
        this.emitGoodRoadSync()
    }

    mergeSocketTable(current: Table, sDesk: Table, type: string): Table | null {
        const _copy = { ...current, expand: { ...current.expand } }

        if (type === SOCKET_MESSAGE.NEW_GAME) {
            _copy.expand.shoeNo = sDesk.expand.shoeNo
            _copy.expand.juNo = sDesk.expand.juNo
            _copy.expand.gameNo = sDesk.expand.gameNo
            _copy.expand.daoJiShiBetTime = sDesk.expand.daoJiShiBetTime
            _copy.expand.stopBet = 0
            _copy.expand.betFormList = []
            _copy.expand.xianAmount = 0
            _copy.expand.xianDuiAmount = 0
            _copy.expand.zhuangAmount = 0
            _copy.expand.zhuangDuiAmount = 0
            _copy.expand.heAmount = 0
            _copy.expand.playerOne = undefined
            _copy.expand.playerTwo = undefined
            _copy.expand.playerThree = undefined
            _copy.expand.bankerOne = undefined
            _copy.expand.bankerTwo = undefined
            _copy.expand.bankerThree = undefined
            _copy.expand.playerCount = undefined
            _copy.expand.bankerCount = undefined
            _copy.expand.whoWin = -1
            _copy.timeStamp = new Date().getTime()
            return _copy
        }

        if (type === SOCKET_MESSAGE.END_GAME) {
            _copy.expand.playerOne = sDesk.expand.playerOne
            _copy.expand.playerTwo = sDesk.expand.playerTwo
            _copy.expand.playerThree = sDesk.expand.playerThree
            _copy.expand.bankerOne = sDesk.expand.bankerOne
            _copy.expand.bankerTwo = sDesk.expand.bankerTwo
            _copy.expand.bankerThree = sDesk.expand.bankerThree
            _copy.expand.playerCount = sDesk.expand.playerCount
            _copy.expand.bankerCount = sDesk.expand.bankerCount
            _copy.expand.whoWin = sDesk.expand.whoWin
            _copy.expand.resultList = sDesk.expand.resultList
            _copy.expand.winOrLoseList = sDesk.expand.winOrLoseList
            _copy.expand.betFormList = []
            _copy.expand.xianAmount = 0
            _copy.expand.xianDuiAmount = 0
            _copy.expand.zhuangAmount = 0
            _copy.expand.zhuangDuiAmount = 0
            _copy.expand.heAmount = 0
            _copy.timeStamp = new Date().getTime()
            return _copy
        }

        if (type === SOCKET_MESSAGE.BET) {
            if (sDesk.expand.memberId !== this.memberId) return null

            const merged = mergeTableBetUpdate(_copy, sDesk)
            const balance = merged.expand?.amount
            if (balance !== undefined && balance !== null) {
                store.commit(UserMutation.SET_BET_COIN, balance)
            }
            return merged
        }

        if (type === SOCKET_MESSAGE.STOP_GAME) {
            _copy.expand.daoJiShiBetTime = 0
            _copy.status = 2
            return _copy
        }

        if (type === SOCKET_MESSAGE.STOP_BET) {
            _copy.expand.stopBet = 1
            if (
                sDesk.expand.amount !== undefined &&
                sDesk.expand.amount !== null
            ) {
                store.commit(UserMutation.SET_BET_COIN, sDesk.expand.amount)
            }
            return _copy
        }

        if (type === SOCKET_MESSAGE.CANCEL_GAME) {
            _copy.expand.betFormList = []
            _copy.status = sDesk.status

            const memberResultList = sDesk.expand.winOrLoseList as
                | ResultMemberList[]
                | undefined
            const mine = memberResultList?.find(
                x => x.memberId === this.memberId
            )
            if (mine) {
                store.commit(UserMutation.SET_BET_COIN, mine.amount)
            }
            return _copy
        }

        if (type === SOCKET_MESSAGE.NEW_SHOE) {
            sDesk.expand.betFormList = []
            const {
                downLimit,
                upLimit,
                limitMax,
                pairDownLimit,
                pairUpLimit,
                tieDownLimit,
                tieUpLimit,
                superSixDownLimit,
                superSixUpLimit
            } = _copy

            sDesk.downLimit = downLimit
            sDesk.upLimit = upLimit
            sDesk.limitMax = limitMax
            sDesk.pairDownLimit = pairDownLimit
            sDesk.pairUpLimit = pairUpLimit
            sDesk.tieDownLimit = tieDownLimit
            sDesk.tieUpLimit = tieUpLimit
            sDesk.superSixDownLimit = superSixDownLimit
            sDesk.superSixUpLimit = superSixUpLimit
            sDesk.expand.resultList = []

            return {
                ..._copy,
                ...sDesk,
                expand: {
                    ..._copy.expand,
                    ...sDesk.expand
                }
            }
        }

        return null
    }

    hideMe(): void {
        this.$emit('on-hide-good-road')
    }

    changeIndex(gameType: string): void {
        if (gameType === this.selectedIndex) return
        this.selectedIndex = gameType
    }

    loadAllData(forceReload = false): void {
        if (!forceReload) this.showLoading()

        this.fetchAllHallData()
            .then(() => this.fetchAndMergeMultiDetail())
            .then(() => {
                if (!forceReload) this.hideLoading()
                this.emitGoodRoadSync()
                this.cancelCountDown()
                this.startCountDown()
            })
            .catch(() => {
                if (!forceReload) this.hideLoading()
            })
    }

    fetchAndMergeMultiDetail(): Promise<void> {
        const tableIds = collectGoodRoadTableIds(this.desksByType)
        if (!tableIds.length) return Promise.resolve()

        return getMultiTableDetail({
            token: this.token,
            memberId: this.memberId,
            tableIds: tableIds.join(','),
            limitId: this.limitId
        })
            .then(r => {
                const response = r.data
                if (response.status !== 200) {
                    if (response.status === 301) {
                        this.stopApiRequest()
                        this.emitter.emit(EventBuss.SESSION_EXPIRED)
                    }
                    return
                }

                const detailTables = response.data.gameTableList || []
                if (!detailTables.length) return

                GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                    this.desksByType[gameTypeNo] = mergeMultiDetailIntoTables(
                        this.desksByType[gameTypeNo] || [],
                        detailTables
                    )
                })
            })
            .catch(error => {
                if (axios.isCancel(error)) return
                throw error
            })
    }

    fetchAllHallData(): Promise<void> {
        return getHallData({
            token: this.token,
            gameTypeNo: ''
        })
            .then(r => {
                const response = r.data
                if (response.status === 200) {
                    this.failureCounter = 0

                    if (response.data.memberLimitList) {
                        store.commit(
                            UserMutation.SET_USER_INFO_LIMITS,
                            response.data.memberLimitList
                        )
                    }

                    this.desksByType = splitGoodRoadTablesByType(
                        response.data.gameTableList || []
                    )
                    return
                }

                if (response.status === 301) {
                    this.stopApiRequest()
                    this.emitter.emit(EventBuss.SESSION_EXPIRED)
                }

                GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                    this.desksByType[gameTypeNo] = []
                })
            })
            .catch(error => {
                if (axios.isCancel(error)) return

                this.failureCounter++
                this.emitter.emit(
                    EventBuss.TOAST_MESSAGE,
                    this.iN('prompt.networkerror')
                )

                if (this.failureCounter > 3) {
                    this.emitter.emit(
                        EventBuss.TOAST_MESSAGE,
                        this.iN('prompt.networkerror')
                    )
                    this.emitter.emit(EventBuss.SOCKET_CLOSE)
                    store.commit(UserMutation.DELETE_SESSION)
                    router.push({ name: ROUTES.LOGIN })
                }

                throw error
            })
    }

    startCountDown(): void {
        if (typeof this.countDownTimer === 'number') return

        this.timerStarted = true
        this.doCountDown()
    }

    doCountDown(): void {
        this.countDownTimer = window.setTimeout(() => {
            GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                const desks = this.desksByType[gameTypeNo] || []

                desks.forEach(item => {
                    if (item.expand.daoJiShiBetTime > 0) {
                        item.expand.daoJiShiBetTime -= 1
                    } else if (item.expand.daoJiShiBetTime === 0) {
                        item.expand.daoJiShiBetTime = -1
                        item.status = 2
                    }
                })
            })

            this.doCountDown()
        }, 1000)
    }

    cancelCountDown(): void {
        if (typeof this.countDownTimer === 'number') {
            clearTimeout(this.countDownTimer)
            this.countDownTimer = false
            this.timerStarted = false
        }
    }

    loadData(forceReload = false): void {
        this.loadAllData(forceReload)
    }

    stopApiRequest(): void {
        this.cancelCountDown()
        if (typeof this.serviceTimer === 'number') {
            clearTimeout(this.serviceTimer)
            this.serviceTimer = false
        }
    }

    emitGoodRoadSync(): void {
        this.$emit('on-good-road-sync', {
            G002: this.desksByType.G002 || [],
            G003: this.desksByType.G003 || []
        })
    }

    get listData(): GoodRoadTableEntry[] {
        const entries = buildPanelListData(
            this.desksByType[this.selectedIndex] || []
        )

        // collect current good-road table ids
        const currentGoodIds = entries
            .filter(e => e.goodRoads.length > 0)
            .map(e => e.table.id)

        // newly detected good roads = present now but not in previous render
        const newGoodIdSet = new Set<number>(
            currentGoodIds.filter(id => !this.prevGoodRoadIds.has(id))
        )

        // prioritize newly detected good-road entries while preserving relative order
        const newGoodEntries = entries.filter(e => newGoodIdSet.has(e.table.id))
        const otherEntries = entries.filter(e => !newGoodIdSet.has(e.table.id))

        // update prev set for next computation
        this.prevGoodRoadIds = new Set<number>(currentGoodIds)

        return newGoodEntries.concat(otherEntries)
    }

    get baccaratGoodRoadCount(): number {
        return countGoodRoadTables(this.desksByType.G002 || [])
    }

    get dragonTigerGoodRoadCount(): number {
        return countGoodRoadTables(this.desksByType.G003 || [])
    }

    onItemBetSuccess(table: Table): void {
        const gameType = table.gameTypeNo
        if (gameType !== 'G002' && gameType !== 'G003') return

        const desks = this.desksByType[gameType] || []
        const index = desks.findIndex(d => d.id === table.id)
        if (index !== -1) {
            desks.splice(index, 1, mergeTableBetUpdate(desks[index], table))

            const balance = table.expand?.amount
            if (balance !== undefined && balance !== null) {
                store.commit(UserMutation.SET_BET_COIN, balance)
            }
        }

        this.emitGoodRoadSync()
    }
}
